import { trackProductInfoProductDetailsVariantSelectionClick } from "@faire/web-api/events/productInfo/click/productDetailsVariantSelection";
import { IProductVariation } from "@faire/web-api/indigofair/data/IProductVariation";
import { IProductVariationMap } from "@faire/web-api/indigofair/data/IProductVariationMap";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React, { useCallback } from "react";

import {
  NUM_ROWS_OF_SWATCHES,
  SWATCH_WIDTH,
} from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/SpecialOptionPicker";
import { SpecialOptionVariation } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/SpecialOptionVariation";
import { VariantOptionSelectionViewState } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/VariantOptionSelectionViewState";

const SWATCH_ROW_TABLET_WIDTH = 300;
const SWATCH_ROW_DEFAULT_WIDTH = 360;

const OptionVariationBase: React.FC<{
  productVariation: IProductVariation;
  viewState: VariantOptionSelectionViewState;
  index: number;
  variationOptions?: IProductVariation[];
  variationDisplayType: keyof typeof IProductVariationMap.IEntry.VariationDisplayType;
  variationName: string;
  hoveredValue?: string;
  setHoveredValue: (value?: string) => void;
  alt: string;
  showAllSwatches: boolean;
}> = ({
  productVariation,
  viewState,
  variationDisplayType,
  variationName,
  index,
  hoveredValue,
  setHoveredValue,
  alt,
  showAllSwatches,
}) => {
  const outOfStock = viewState.isVariationOutOfStock(productVariation);
  const productOption = viewState.optionForAlternateVariation(productVariation);
  const unavailable = !productOption;
  const selectedVariationValue =
    viewState.getSelectedVariationValue(variationName);

  const handleVariationClick = useCallback(() => {
    trackProductInfoProductDetailsVariantSelectionClick(
      variationDisplayType.toString()
    );
    viewState.handleVariationSelect(variationName, productVariation.value!);
  }, [productVariation.value, variationDisplayType, variationName, viewState]);

  const variationMap = viewState.product.purchase_information?.variation_map;

  const { isTablet } = useViewport();
  const numSwatchesPerRow = Math.floor(
    (isTablet ? SWATCH_ROW_TABLET_WIDTH : SWATCH_ROW_DEFAULT_WIDTH) /
      SWATCH_WIDTH
  );

  if (productVariation.value === undefined) {
    return null;
  }

  return (
    <SpecialOptionVariation
      key={productVariation.value}
      onVariationClick={handleVariationClick}
      variationValue={productVariation.value}
      outOfStock={outOfStock}
      unavailable={unavailable}
      productOption={productOption}
      selected={selectedVariationValue === productVariation.value}
      displayType={variationDisplayType}
      hovered={hoveredValue === productVariation.value}
      setHoveredValue={setHoveredValue}
      alt={alt}
      imageUrl={
        variationMap?.entries
          .find((entry) => entry.name === variationName)
          ?.entry_values?.find(
            (entryValue) => entryValue.value === productVariation.value
          )?.image?.url
      }
      isHidden={
        !showAllSwatches && index >= NUM_ROWS_OF_SWATCHES * numSwatchesPerRow
      }
    />
  );
};

export const OptionVariation = observer(OptionVariationBase);
